import { useState, useEffect, useRef, useCallback } from 'react';
import { notificationsApi } from '../services/api/endpoints/notifications';
import { CircuitBreaker } from '../services/CircuitBreaker';
import type { Notification } from '../types/notifications';

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const loadingRef = useRef(false);
  const circuitBreaker = CircuitBreaker.getInstance('notifications', 3, 60000); // 1 minute

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    if (loadingRef.current) {
      return; // Éviter les appels concurrents
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      
      const response = await circuitBreaker.execute(async () => {
        return await notificationsApi.getNotifications();
      });
      
      if (response.success && response.data) {
        setNotifications(response.data.notifications);
        setUnreadCount(response.data.unreadCount);
      } else {
        throw new Error(response.error || 'API Error');
      }
    } catch (err) {
      console.warn('🔄 Notifications API failed, using empty fallback data');
      // PRODUCTION: Utiliser des données vides au lieu des mocks
      setNotifications([]);
      setUnreadCount(0);
      setError(err instanceof Error ? err.message : 'API non disponible');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const addNotification = useCallback(async (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    try {
      const response = await circuitBreaker.execute(async () => {
        return await notificationsApi.createNotification({
          type: notification.type,
          title: notification.title,
          message: notification.message,
          link: notification.link
        });
      });

      if (response.success && response.data) {
        setNotifications(prev => {
          const safePrev = Array.isArray(prev) ? prev : [];
          return [response.data!, ...safePrev];
        });
        setUnreadCount(prev => prev + 1);
        return response.data;
      } else {
        throw new Error(response.error || 'Erreur lors de la création de la notification');
      }
    } catch (err) {
      console.warn('🔄 Failed to create notification via API, creating locally:', err);
      
      // Fallback vers la création locale
      const newNotification: Notification = {
        ...notification,
        id: crypto.randomUUID(),
        timestamp: new Date().toISOString(),
        read: false
      };

      setNotifications(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];
        return [newNotification, ...safePrev];
      });
      setUnreadCount(prev => prev + 1);
      return newNotification;
    }
  }, [circuitBreaker]);

  const markAsRead = async (id: string) => {
    try {
      const response = await notificationsApi.markAsRead(id);
      if (response.success) {
        setNotifications(prev => {
          const safePrev = Array.isArray(prev) ? prev : [];
          return safePrev.map(notification =>
            notification.id === id ? { ...notification, read: true } : notification
          );
        });
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Erreur lors du marquage comme lu:', err);
      // Fallback local en cas d'erreur API
      setNotifications(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];
        return safePrev.map(notification =>
          notification.id === id ? { ...notification, read: true } : notification
        );
      });
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = async () => {
    try {
      const safeNotifications = Array.isArray(notifications) ? notifications : [];
      const unreadIds = safeNotifications.filter(n => !n.read).map(n => n.id);
      if (unreadIds.length === 0) return;
      
      // Utiliser l'endpoint markAllAsRead au lieu de markAsRead avec IDs concaténés
      const response = await notificationsApi.markAllAsRead();
      if (response.success) {
        setNotifications(prev => {
          const safePrev = Array.isArray(prev) ? prev : [];
          return safePrev.map(notification => ({ ...notification, read: true }));
        });
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Erreur lors du marquage global comme lu:', err);
      // Fallback local en cas d'erreur API
      setNotifications(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];
        return safePrev.map(notification => ({ ...notification, read: true }));
      });
      setUnreadCount(0);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      const response = await notificationsApi.deleteNotification(id);
      if (response.success) {
        setNotifications(prev => {
          const safePrev = Array.isArray(prev) ? prev : [];
          return safePrev.filter(notification => notification.id !== id);
        });
        setUnreadCount(prev => {
          const safeNotifications = Array.isArray(notifications) ? notifications : [];
          const notification = safeNotifications.find(n => n.id === id);
          return notification && !notification.read ? prev - 1 : prev;
        });
      }
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      // Fallback local en cas d'erreur API
      setNotifications(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];
        return safePrev.filter(notification => notification.id !== id);
      });
      setUnreadCount(prev => {
        const safeNotifications = Array.isArray(notifications) ? notifications : [];
        const notification = safeNotifications.find(n => n.id === id);
        return notification && !notification.read ? prev - 1 : prev;
      });
    }
  };

  const clearAll = async () => {
    try {
      const response = await notificationsApi.deleteAllNotifications();
      if (response.success) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Erreur lors de la suppression globale:', err);
      // Fallback local en cas d'erreur API
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const updatePreferences = async (preferences: any) => {
    try {
      const response = await notificationsApi.updateNotificationPreferences(preferences);
      return response.success;
    } catch (err) {
      console.error('Erreur lors de la mise à jour des préférences:', err);
      return false;
    }
  };

  const getPreferences = async () => {
    try {
      const response = await notificationsApi.getNotificationPreferences();
      return response.success ? response.data : null;
    } catch (err) {
      console.error('Erreur lors de la récupération des préférences:', err);
      return null;
    }
  };

  const testNotification = async (type: string = 'info') => {
    try {
      const response = await notificationsApi.sendTestNotification(type);
      return response.success;
    } catch (err) {
      console.error('Erreur lors du test de notification:', err);
      return false;
    }
  };

  return {
    notifications,
    loading,
    error,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    updatePreferences,
    getPreferences,
    testNotification,
    refreshNotifications: loadNotifications
  };
}
